import type { CacheBackend } from "@pdfking/compatibility"
import type { AnyGUID, StringGUID } from "@pdfking/tfunc"
import { GuidToString, ProcessIterable, ReplayBroadcast } from "@pdfking/tfunc"

import type { MessageRouter } from "../types/MessageRouter"
import type { ReceiverInput } from "../types/ReceiverInput"
import type { ReceiverOutput } from "../types/ReceiverOutput"

const LockedForward = <T, R>(
  lock_by: (
    input: ReceiverInput<T>
  ) => Promise<[StringGUID, () => ReceiverOutput<R>]>
): MessageRouter<T, R> => {
  const channels: Map<
    StringGUID,
    ReturnType<typeof ReplayBroadcast<R>>
  > = new Map()

  return async function* (request: ReceiverInput<T>) {
    const [key, next] = await lock_by(request)

    const channel = channels.get(key)

    if (channel) {
      yield* channel.subscribe()
    } else {
      const channel = ReplayBroadcast<R>()
      channels.set(key, channel)

      void ProcessIterable(next(), channel.push, async (error?: Error) => {
        channel.stop(error)
        channels.delete(key)
      })
      yield* channel.subscribe()
    }
  }
}

const ApplyCache = async function* <R>(
  id: StringGUID,
  stream: () => ReceiverOutput<R>,
  cache?: CacheBackend<R[]>,
  cache_evict?: boolean
): ReceiverOutput<R> {
  if (cache) {
    const existing: R[] | undefined | void = cache_evict
      ? await cache.delete(id)
      : await cache.get(id)

    if (existing) {
      yield* existing
    } else {
      const messages = []
      for await (const message of stream()) {
        messages.push(message)
        yield message
      }
      await cache.set(id, messages)
    }
  } else {
    yield* stream()
  }
}

export const CachedForward = <T, R>(
  forward: MessageRouter<T, R>,
  resolver: (
    input: ReceiverInput<T>
  ) => Promise<{
    guid: AnyGUID
    input: ReceiverInput<T>
    cache_evict?: boolean
  }>,
  cache?: CacheBackend<R[]>
): MessageRouter<T, R> =>
  LockedForward(async input => {
    const { guid, input: next, cache_evict } = await resolver(input)
    const key = GuidToString(guid)
    return [key, () => ApplyCache(key, () => forward(next), cache, cache_evict)]
  })
